import { Meteor } from 'meteor/meteor';
import { Mongo } from 'meteor/mongo';
import { DeclareMethod } from '/imports/methods';
import { pluck } from '/imports/utils';
import { Doc, GetMethodResponse, ListMethodArg, ListMethodResponse, ModelClass, References } from '/imports/types';
import { validateBoolean, validateString } from '/imports/validators';
import { SolarEdgeSite } from '/server/integrations/solarEdge';

export interface SolarEdgeMeter extends Doc, Omit<SolarEdgeSite, 'name'> {
  _id: string;
  meterId: string;
  apiKeyId: string;
}
export type Model = SolarEdgeMeter;

export const SolarEdgeMeters = new Mongo.Collection<SolarEdgeMeter>('solarEdgeMeters');

type Refs = References<Model>

export const modelClass: ModelClass<Model, Refs> = {
  collection: SolarEdgeMeters,
  editableFields: ['active'],
  listMethod: DeclareMethod<ListMethodArg<SolarEdgeMeter>, ListMethodResponse<SolarEdgeMeter, Refs>>('listSolarEdgeMeters'),
  getMethod: DeclareMethod<string, GetMethodResponse<SolarEdgeMeter, Refs>>('getSolarEdgeMeter'),
  createMethod: DeclareMethod<Partial<SolarEdgeMeter>, string>('createSolarEdgeMeter'),
  updateMethod: DeclareMethod<Partial<SolarEdgeMeter>, string>('updateSolarEdgeMeter'),
  deleteMethod: DeclareMethod<string>('deleteSolarEdgeMeter'),
  pathPrefix: '/solaredge-meters',
  asString: (document) => document.name || document.apiKey || document._id,
  modelName: 'SolarEdge',
  pluralName: 'SolarEdges',
  searchQuery: (search?: string) => {
    search = search?.trim();
    if (!search?.length) return {};
    return {
      $or: [
        { _id: search },
        { name: new RegExp(search, 'gi') },
        { identifier: new RegExp(search, 'gi') },
      ]
    };
  },
  validate: async (document: Partial<SolarEdgeMeter>) => {
    const errors: { [key: string]: string[] } = {};
    validateBoolean(errors, document, 'active');
    validateString(errors, document, 'meterId', { required: true });
    return errors;
  },
};

export const pullSolarEdgeMeterData = DeclareMethod<{ _id: string, days?: number }>('pullSolarEdgeMeterData');

Meteor.isServer && Meteor.startup(async () => {
  await SolarEdgeMeters.createIndexAsync({ meterId: 1 });
});

export interface SolarEdgeApiKey extends Doc {
  _id: string;
  orgId: string;
  apiKey: string;
}

export const SolarEdgeApiKeys = new Mongo.Collection<SolarEdgeApiKey>('solarEdgeApiKeys');

export const createSolarEdgeApiKey = DeclareMethod<Partial<SolarEdgeApiKey>, string>('createSolarEdgeApiKey');
export const updateSolarEdgeApiKey = DeclareMethod<Partial<SolarEdgeApiKey>>('updateSolarEdgeApiKey');
/** What a listing may show: the ends of the key for recognition, never the key. */
export interface SolarEdgeApiKeyListing {
  _id: string;
  orgId: string;
  apiKeyFirst: string;
  apiKeyLast: string;
}
export const getSolarEdgeApiKeysForOrg = DeclareMethod<string, SolarEdgeApiKeyListing[]>('getSolarEdgeApiKeysForOrg');
export const pullSolarEdgeSites = DeclareMethod<string, { numSystems: number; numCreated: number; }>('pullSolarEdgeSites');

/**
 * Validates a SolarEdge API key, requiring an org and a key.
 * @param apiKey - partial API key to validate
 * @returns map of field name to validation error messages
 */
export const validateSolarEdgeApiKey = (apiKey: Partial<SolarEdgeApiKey>) => {
  const errors: { [key: string]: string[] } = {};
  validateString(errors, apiKey, 'orgId', { required: true });
  validateString(errors, apiKey, 'apiKey', { required: true });
  return errors;
};

/**
 * Strips an API key down to its persistable fields.
 * @param apiKey - partial API key to clean
 * @returns the API key with only allowed fields
 */
export const cleanSolarEdgeApiKey = (apiKey: Partial<SolarEdgeApiKey>) => pluck(apiKey, ['orgId', 'apiKey']) as SolarEdgeApiKey;